import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Fuse from "fuse.js";
import classnames from "classnames";

import useDebounce from "../../../../../hooks/useDebounce";
import useFuzzySearch from "../../../../../hooks/useFuzzySearch";
import useTranslations from "../../../../../hooks/useTranslations";
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";

import { useWombatTheme } from "../../../theme";
import { FieldError } from "react-hook-form";
import { ChevronDownIcon } from "../../../../icons";
import { TailSpin } from "../../Loader/TailSpin";
import { isEqual } from "lodash";
import { ReactHookFormProps } from ".";

export type SelectSingleSearchableProps<T> = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * Select id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: `select_${id}_group`
   *  - Label: `select_${id}_label`
   *  - Searchable input: `search_input_${id}` 
   * 
   *    *The sole purpose of this input is to display the selected item 
   *   It is render as an input to allow for native interactions with React Hook Form*
   *  - Selected item input: `select_item_input_${id}` 
   * 
   *  - Dropdown container: `dropdown_container_${id}` 
   * 
   *   *Each items will be attributed a data-test-id based on their index*
   *  - Items: `select_${id}_item_${value}`
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Loading state, used when the page can be shown even tho data for select is not already loaded
   * @default false
   */
  loading?: boolean;
  /**
   * Options of the select
   */
  items: T[];
  /**
   * Function that give back the value from an item T, used internally to retrieve the current value
   */
  getValue: (item: T) => string | number;
  /**
   * This function is triggered each time a change to the value is made
   * @default undefined
   */
  onChange?: (item: T) => void;
  /**
   * Default value of the select
   */
  defaultValue?: T;
  /**
   * Placeholder
   */
  placeholder?: string;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Set to true to have a Fuzzy search on you're select
   * NOTE:
   * - As of now, accessibility pattern are not good with this sort of select so it might evolve in a near future
   * - The function signature of `renderItem` change to accept `Fuse.IFuseOptions<T>` in place of `T`
   */
  searchable: true;
  /**
   * Only available with searchable set to true
   * NOTE: matchIfEmpty props is forced to be true
   */
  searchOption: Fuse.IFuseOptions<T>;
  /**
   * This function is called to render elements inside the select dropdown
   */
  renderItem: (item: T) => string;
  /**
   * Used to render an empty search
   * @default t('wombat.select.emptyResult')
   */
  emptyResult?: () => React.ReactNode;
  /**
   * This is the prefered way of dealing with a select using react-hook-form.
   * It make use of the pattern described [here](https://react-hook-form.com/advanced-usage/#ConditionalControlledComponent)
   * Follow the pattern described in ReactHookFormProps to pass props to your component
   */
  RHF?: ReactHookFormProps;
};

/**
 * A Select component that accept a single value as the answer
 * The component internally store the value inside an input allowing for basic validation through react-hook-form
 */
const SelectSingleSearchable = <T extends unknown>({
  label,
  id,
  loading = false,
  srOnlyLabel,
  disabled = false,
  items,
  defaultValue,
  getValue,
  onChange,
  placeholder,
  error,
  renderItem,
  searchOption,
  emptyResult,
  RHF,
}: SelectSingleSearchableProps<T>) => {
  const t = useTranslations();
  const { theme } = useWombatTheme();

  const _defaultValue = useMemo((): Fuse.FuseResult<T> | null => {
    if (!defaultValue) return null;
    return {
      item: defaultValue,
      refIndex: items.findIndex((i) => getValue(i) === getValue(defaultValue)),
    };
  }, [defaultValue, items]);

  const labelStyle = classnames(
    theme.form.select.label.base,
    disabled ? theme.form.select.label.disabled : theme.form.select.label.able,
    srOnlyLabel && "sr-only"
  );
  const selectStyle = classnames(
    theme.form.select.base,
    theme.form.select.status[error?.message ? "error" : "default"],
    "relative"
  );
  const internaInputStyle = classnames(
    "absolute inset-x-3 inset-y-0 pr-6 outline-none appearance-none focus:outline-none disabled:bg-transparent pointer-events-none truncate"
  );
  const _emptyResult = useMemo(
    () => (emptyResult ? emptyResult : () => t("wombat.select.emptyResult")),
    [emptyResult]
  );

  useEffect(() => {
    if (RHF) {
      RHF.register(RHF.name, RHF.registerOptions);
      return () => {
        RHF.unregister(RHF.name);
      };
    }
  }, [RHF?.register, RHF?.unregister, RHF?.name]);

  const [dropOpen, setDropOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(() => {
    setDropOpen(false);
  }, []);

  useOnClickOutside(popoverRef, handleClickOutside);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchInputFocus = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [searchInputRef]);
  // const handleCaretClick = useCallback(() => {
  //   if (searchInputRef.current && !dropOpen) searchInputRef.current.focus();
  //   else setDropOpen(false);
  // }, [searchInputRef, dropOpen]);

  const [value, setValue] = useState("");
  const [_item, setItem] = useState<Fuse.FuseResult<T> | null>(_defaultValue);
  const debouncedValue = useDebounce(value, 200);

  /**
   * Reset selected items in case items change
   */
  useEffect(() => {
    setItem(_defaultValue);
    if (RHF)
      RHF.setValue(
        RHF.name,
        _defaultValue ? getValue(_defaultValue.item) : undefined,
        RHF.setValueOptions
      );
  }, [items]);

  const results = useFuzzySearch<T>(items, debouncedValue, {
    ...searchOption,
    matchIfEmpty: true,
  });

  const handleItemSelected = useCallback(
    (item: Fuse.FuseResult<T>) => {
      setValue("");
      if (!isEqual(item.item, _item?.item)) {
        if (onChange && typeof onChange === 'function') onChange(item.item);
        if (RHF)
          RHF.setValue(RHF.name, getValue(item.item), RHF.setValueOptions);
      }
      setItem(item);
      setDropOpen(false);
    },
    [_item, RHF]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    },
    []
  );

  return (
    <div ref={popoverRef}>
      <label className={labelStyle} htmlFor={id} data-test-id={`select_${id}_label`}>
        {label}
      </label>
      <div
        className={selectStyle}
        onClick={handleSearchInputFocus}
        data-test-id={`select_${id}_group`}
      >
        <input
          className="w-full pr-6 outline-none appearance-none focus:outline-none"
          onFocus={() => setDropOpen(true)}
          onChange={handleInputChange}
          ref={searchInputRef}
          placeholder={!_item ? placeholder : undefined}
          value={value}
          data-test-id={`search_input_${id}`}
        />
        {_item && !value && (
          <input
            disabled
            className={internaInputStyle}
            value={renderItem(_item.item)}
            data-test-id={`select_item_input_${id}`}
            style={{ width: "calc(100% - 0.75rem)" }}
          />
        )}
        <span className="absolute flex items-center w-5 h-5 transform -translate-x-8 inset-x-full">
          <ChevronDownIcon iconClassName="text-primary-900 w-4 h-4" />
        </span>
      </div>
      <div className="relative">
        <div
          data-test-id={`dropdown_container_${id}`}
          className={`${
            dropOpen ? "block" : "hidden"
          } absolute inset-0 z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-56`}
        >
          {!loading && (
            <ul
              tabIndex={-1}
              role="listbox"
              className="flex flex-col py-1 overflow-auto text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 max-h-56 focus:outline-none sm:text-sm"
            >
              {/* NOTE: Will probably need to be virtualized when huge data set is passed */}
              {results.length > 0 &&
                results.map((item, index) => (
                  <li
                    role="button"
                    onClick={() => handleItemSelected(item)}
                    className={
                      theme.form.select.item[
                        index === _item?.refIndex ? "selected" : "base"
                      ]
                    }
                    key={getValue(item.item)}
                    data-test-id={`select_${id}_item_${getValue(item.item)}`}
                  >
                    {renderItem(item.item)}
                  </li>
                ))}
              {results.length === 0 && _emptyResult()}
            </ul>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center flex-grow w-full h-32 bg-white rounded-md shadow-md">
              <TailSpin className="w-6 h-6 text-primary-600" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SelectSingleSearchable);
